import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DesktopSidebar } from './DesktopSidebar';
import { MobileBottomNav } from './MobileBottomNav';
import { useSession } from '../lib/auth';
import { useViewingAs } from '../lib/useViewingAs';

export type Viewport = 'phone' | 'tablet' | 'desktop';

export function useViewport(): Viewport {
  const get = (): Viewport => {
    const w = window.innerWidth;
    if (w >= 1024) return 'desktop';
    if (w >= 721) return 'tablet';
    return 'phone';
  };
  const [size, setSize] = useState<Viewport>(get);
  useEffect(() => {
    const onResize = () => setSize(get());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

type Props = { children: ReactNode };

export function ResponsiveShell({ children }: Props) {
  const size = useViewport();
  const location = useLocation();
  const { status } = useSession();

  // Hide the nav on the login/privacy routes and before the user has loaded.
  const showMobileNav =
    size === 'phone' &&
    status === 'ready' &&
    !location.pathname.startsWith('/login') &&
    !location.pathname.startsWith('/privacy');

  if (size === 'phone') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--paper)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ActingAsBanner />
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {children}
        </div>
        {showMobileNav && <MobileBottomNav />}
      </div>
    );
  }

  if (size === 'tablet') {
    return (
      <div
        className="canvas-bg"
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <div
          style={{
            width: 480,
            height: '100%',
            maxHeight: 920,
            background: 'var(--paper)',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 0 0 1.5px var(--ink), 8px 8px 0 rgba(20,16,10,0.4)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <ActingAsBanner />
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className="canvas-bg"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '240px 1fr',
          width: '100%',
          maxWidth: 1100,
          height: '100%',
          maxHeight: 920,
          gap: 0,
          boxShadow: '0 0 0 1.5px var(--ink), 10px 10px 0 rgba(20,16,10,0.4)',
          background: 'var(--paper)',
          overflow: 'hidden',
        }}
      >
        <div
          className="canvas-bg"
          style={{
            borderRight: '2px solid var(--ink)',
            display: 'flex',
            flexDirection: 'column',
            color: 'var(--cream)',
          }}
        >
          <DesktopSidebar />
        </div>
        <div
          style={{
            background: 'var(--paper)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <ActingAsBanner />
          {children}
        </div>
      </div>
    </div>
  );
}

// Sticky gold banner that rides above every screen while ?as=<kid_id> is
// in the URL. Stays put so the user can never forget which mode they're in.
function ActingAsBanner() {
  const location = useLocation();
  const { asKid } = useViewingAs();
  if (!asKid) return null;
  // "Back to my view" preserves the current path but drops the as= param.
  const backTo = location.pathname;
  return (
    <div
      style={{
        background: 'var(--gold, #D4A846)',
        borderBottom: '1.5px solid var(--ink)',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontFamily: 'JetBrains Mono',
          fontSize: 11,
          letterSpacing: '0.16em',
          color: 'var(--ink)',
          textTransform: 'uppercase',
        }}
      >
        ★ Acting as {asKid.display_name}
      </div>
      <Link
        to={backTo}
        style={{
          fontFamily: 'JetBrains Mono',
          fontSize: 10,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--ink)',
          background: 'transparent',
          border: '1.5px solid var(--ink)',
          padding: '4px 8px',
          textDecoration: 'none',
        }}
      >
        ✕ Back to my view
      </Link>
    </div>
  );
}
